import { useGetAgendaByIdQuery } from 'store/services/flow/agenda';
import { useGetUserViewQuery } from 'store/services/flow/views';
import type { DataSchema, InlineRecordType, ModalsType, RecordsViewParam, Schema } from 'types';

import { memo, useCallback, useMemo, useState } from 'react';
import { Tabs } from 'components/mui/common/Tabs';
import { useTranslation } from 'react-i18next';
import { useLocaleCodeIsoNorma } from 'utils/hooks';

import { formatDisplayValue } from 'utils/agendaRecord/formatDisplayValue';
import { getSelectRowId } from 'utils/table.ts';
import { UserAgendaTable } from 'components/user/UserAgendaTable';
import { ActionModal } from 'components/mui/common/ActionModal';
import { normalizeSelectedRows } from '../utils/selectedRows';
import type { SelectedRow, SelectedRowInput } from '../utils/selectedRows';

interface ModalProps extends ModalsType {
  agendaId: string;
  viewId: string;
  recordId?: string;
  parentAgendaId?: string;
  title: string;
  isMultiSelect: boolean;
  selectedRows?: SelectedRowInput[];
  onSubmit: (e: SelectedRow[]) => void;
  params?: RecordsViewParam[];
  formItemIdToShow: string;
  overrideCalculateValues?: InlineRecordType[];
  initialSearchTerm?: string;
  isAutoSelectSearchResultEnabled?: boolean;
  onAutoSelectConsumed?: () => void;
}

export const RecordModal = memo(
  ({
    open,
    onClose,
    title,
    onSubmit,
    agendaId,
    recordId,
    parentAgendaId,
    viewId,
    isMultiSelect,
    selectedRows: selectedRowsProps = [],
    params,
    formItemIdToShow,
    overrideCalculateValues = [],
    initialSearchTerm = '',
    isAutoSelectSearchResultEnabled = false,
    onAutoSelectConsumed = () => undefined,
  }: ModalProps) => {
    const { t } = useTranslation();
    const locale = useLocaleCodeIsoNorma();
    const [selectedRows, setSelectedRows] = useState<SelectedRow[]>(() => normalizeSelectedRows(selectedRowsProps));

    const { data: { viewName } = {} } = useGetUserViewQuery(
      {
        parentId: agendaId,
        viewId,
        viewParentType: 'Agenda',
      },
      {
        skip: !agendaId || !viewId,
      }
    );

    const { data: agendaData } = useGetAgendaByIdQuery(
      {
        id: agendaId,
      },
      { skip: !agendaId }
    );

    const isHeliosTable = useMemo(() => !!agendaData?.module?.isHeliosVisible, [agendaData]);

    const handleSelectRows = useCallback(
      (rowIds: string[], rows?: Schema<string | number, DataSchema>[]) => {
        const newItems =
          rows?.map((row) => ({
            id: getSelectRowId(row, isHeliosTable),
            value: formatDisplayValue(row[formItemIdToShow], isHeliosTable, locale),
          })) ?? [];

        setSelectedRows((prevState) => {
          const selectedMap = new Map([...prevState, ...newItems].map((item) => [item.id, item]));

          return rowIds.map((id) => selectedMap.get(id)!).filter(Boolean);
        });
      },
      [formItemIdToShow, isHeliosTable, locale]
    );

    const handleAutoSelectSingleResult = useCallback(
      (rowId: string, row: Schema<string | number, DataSchema>) => {
        onAutoSelectConsumed();
        const entry = { id: rowId, value: formatDisplayValue(row[formItemIdToShow], isHeliosTable, locale) };
        const next = isMultiSelect ? [...normalizeSelectedRows(selectedRowsProps), entry] : [entry];
        onSubmit(next);
      },
      [formItemIdToShow, isHeliosTable, isMultiSelect, onAutoSelectConsumed, onSubmit, selectedRowsProps, locale]
    );

    return (
      <ActionModal
        open={open}
        title={title}
        onClose={onClose}
        onCancel={onClose}
        onSubmit={() => onSubmit(selectedRows)}
        maxWidth="lg"
        submitText={t('common.save')}>
        <Tabs
          tabs={[{ id: viewId, label: viewName ?? '' }].map(({ id, label }) => ({
            id,
            label,
            path: '',
            content: (
              <UserAgendaTable
                agendaId={agendaId}
                viewId={viewId}
                recordId={recordId}
                parentAgendaId={parentAgendaId}
                disableRowClick
                isOverview
                isModal
                onSelectRow={handleSelectRows}
                isMultiSelect={isMultiSelect}
                selectedRows={selectedRows.map((row) => row.id)}
                params={params}
                modalOverrideCalculateValues={overrideCalculateValues}
                showAllRows
                initialSearchTerm={initialSearchTerm}
                isAutoSelectSingleResult={isAutoSelectSearchResultEnabled}
                onAutoSelectSingleResult={handleAutoSelectSingleResult}
              />
            ),
          }))}
          selectedTab={viewId}
          onTabClick={() => null}
        />
      </ActionModal>
    );
  }
);
